import { LitElement, html, type PropertyValues } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import toArray from '@loadsmart/miranda-toolset/dist/to-array';

import { TOGGLE_EVENT } from './toggle-group.constants';
import SelectionController from '../../controllers/selection';
import styles from './toggle-group.styles';
import type {
	AcceptedType,
	SelectionType,
	SelectionState,
} from '../../controllers/selection';
import type { Toggle } from './toggle';

import '../layout/group';

export type ToggleSize = 'small' | 'default';

export type ToggleGroupChangeEventDetail = {
	type: SelectionType;
	value: SelectionState;
};

export interface ToggleGroupProps {
	disabled?: boolean;
	showCheck?: boolean;
	size?: ToggleSize;
	type: SelectionType;
	value?: AcceptedType | AcceptedType[];
	'm-change'?: (event: CustomEvent<ToggleGroupChangeEventDetail>) => void;
}

export class ToggleGroup extends LitElement {
	static override styles = [styles()];

	#selectionController: SelectionController | null = null;

	#defaultSlot: HTMLSlotElement | null = null;

	static override get properties() {
		return {
			disabled: { type: Boolean },
			role: { type: String, reflect: true },
			showCheck: { type: Boolean, attribute: 'show-check' },
			size: { type: String },
			type: { type: String },
			value: {},
			selection: { state: true },
		};
	}

	/**
	 * Should the toggle group be disabled.
	 */
	declare disabled: boolean;

	/**
	 * Aria role
	 */
	declare role: 'group' | 'radiogroup';

	/**
	 * Should show the check icon when selected.
	 * The check icon is the default content for the `leading` slot.
	 */
	declare showCheck: boolean;

	/**
	 * Strategy for toggling.
	 */
	declare type: SelectionType;

	/**
	 * Toggle group value.
	 */
	declare value?: AcceptedType | AcceptedType[];

	/**
	 * Button size.
	 */
	declare size: ToggleSize;

	/**
	 * Inner toggle selection state.
	 */
	declare selection: SelectionState;

	constructor() {
		super();

		this.showCheck = false;
		this.size = 'default';
		this.type = 'single';
		this.selection = new Set();
	}

	protected override update(changedProperties: PropertyValues<this>): void {
		super.update(changedProperties);

		/**
		 * [!] This flow covers a toggle group changing its selection `type` after being mounted
		 * and having its selection controller initialized.
		 */
		if (
			(changedProperties.get('type') != null && this.type != null) ||
			this.#selectionController == null
		) {
			this.#selectionController = new SelectionController(
				this,
				this.type,
				TOGGLE_EVENT,
			);

			this.#selectionController.init(toArray(this.value));

			// eslint-disable-next-line lit/no-property-change-update -- FIXME
			this.role = ['single', 'single-strict'].includes(this.type)
				? 'radiogroup'
				: 'group';
		}

		if (changedProperties.has('value')) {
			this.#selectionController.init(toArray(this.value));
		}
	}

	protected override updated(changedProperties: PropertyValues<this>): void {
		super.updated(changedProperties);

		if (changedProperties.has('selection')) {
			this.#getToggles().forEach((toggle) => {
				this.#setToggleChecked(toggle);
			});
		}

		if (changedProperties.has('disabled')) {
			this.#getToggles().forEach((toggle) => {
				this.#setToggleDisabled(toggle);
			});
		}

		if (changedProperties.has('role')) {
			this.#getToggles().forEach((toggle) => {
				this.#setToggleRole(toggle);
			});
		}
	}

	override render() {
		return html`
			<m-group
				class=${classMap({
					'toggle-group': true,
					'-single': this.type === 'single',
					'-single-strict': this.type === 'single-strict',
					'-multiple': this.type === 'multiple',

					'-small': this.size === 'small',

					'-show-check': this.showCheck,
				})}
				gap="spacing-1"
			>
				<slot></slot>
			</m-group>
		`;
	}

	#getToggles = (): Toggle[] => {
		this.#defaultSlot ??=
			this.shadowRoot && this.shadowRoot.querySelector('slot');

		const items = this.#defaultSlot?.assignedElements({ flatten: true }) ?? [];

		return [...items].filter(
			(item) => item.tagName.toLowerCase() === 'm-toggle',
		) as Toggle[];
	};

	#setToggleRole = (toggle: Toggle) => {
		toggle.setAttribute(
			'role',
			['single', 'single-strict'].includes(this.type) ? 'radio' : 'checkbox',
		);
	};

	#setToggleChecked = (toggle: Toggle) => {
		// eslint-disable-next-line no-param-reassign
		toggle.checked = this.selection.has(toggle.value);
	};

	#setToggleDisabled = (toggle: Toggle) => {
		// eslint-disable-next-line no-param-reassign
		toggle.disabled = this.disabled;
	};
}

window.customElements.define('m-toggle-group', ToggleGroup);

declare global {
	interface HTMLElementTagNameMap {
		'm-toggle-group': ToggleGroup;
	}
}
